import PropTypes from 'prop-types';
import React from 'react';
import cn from 'classnames';
import { InView } from 'react-intersection-observer';
import { cardShape, promoShape } from '@bibliocommons/bc-prop-types';
import { personalizedPromotionsEventComposer } from '@bibliocommons/analytics-event-composers';
import { sendAnalyticsEvents } from '@bibliocommons/utils-analytics';

import ContentCardType from './ContentCardType';
import ContentCardImage from './ContentCardImage';
import ContentCardTitle from './ContentCardTitle';
import ContentCardDescription from './ContentCardDescription';
import DebugCardButton from './DebugCardButton';
import EventDateStamp from './EventDateStamp';
import EventDateTime from './EventDateTime';
import EventLocation from './EventLocation';

import './ContentCard.scss';

const { composeImpressionEvent, composeTitleClickEvent, composeImageClickEvent } = personalizedPromotionsEventComposer;

export default function ContentCard({
  className,
  card,
  compact,
  debugEnabled,
  headingTag,
  promoSearchRequestId,
  position,
  promo,
  modalActions
}) {
  const classNames = cn('cp-content-card', className, {
    'content-card--compact': compact
  });

  function getAnalyticsPayload() {
    return {
      postType: card.get('postType'),
      cardId: card.get('id'),
      title: card.getIn(['title', 'text']),
      contentType: card.getIn(['contentType', 'slug']),
      contentCreator: card.getIn(['author', 'name']),
      recommendationType: promo.get('recommendationType'),
      requestId: promoSearchRequestId,
      position
    };
  }

  function handleVisibility(isVisible) {
    if (isVisible) {
      sendAnalyticsEvents(composeImpressionEvent(getAnalyticsPayload()));
    }
  }

  function handleTitleClick() {
    sendAnalyticsEvents(composeTitleClickEvent(getAnalyticsPayload()));
  }

  function handleImageClick() {
    sendAnalyticsEvents(composeImageClickEvent(getAnalyticsPayload()));
  }

  return (
    <InView className={classNames} threshold={1} onChange={handleVisibility} triggerOnce>
      {card.getIn(['event', 'startTime']) ? (
        <EventDateStamp card={card} compact={compact} />
      ) : (
        <ContentCardImage card={card} compact={compact} handleClick={handleImageClick} />
      )}
      <div className="card-details">
        <ContentCardType card={card} recommendationType={promo.get('recommendationType')} />
        <ContentCardTitle card={card} tagName={headingTag} handleClick={handleTitleClick} />
        {!compact && <ContentCardDescription card={card} />}
        <EventDateTime card={card} />
        <EventLocation card={card} />
        {debugEnabled && <DebugCardButton card={card} promo={promo} modalActions={modalActions} />}
      </div>
    </InView>
  );
}

ContentCard.propTypes = {
  className: PropTypes.string,
  card: cardShape.isRequired,
  compact: PropTypes.bool,
  headingTag: PropTypes.string,
  promo: promoShape.isRequired,
  position: PropTypes.number.isRequired,
  debugEnabled: PropTypes.bool,
  promoSearchRequestId: PropTypes.string.isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired
};
